import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Reveal, Label } from "./Reveal";
import { scrollToId } from "./SmoothScroll";
import { SERVICES } from "../data";

export default function Services() {
  const [active, setActive] = useState(SERVICES[0].id);
  const activeService = SERVICES.find((s) => s.id === active);

  return (
    <section
      id="services"
      data-testid="services-section"
      className="bg-obsidian py-24 lg:py-32 border-b border-border"
    >
      <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
        <Reveal className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
          <div>
            <Label>Capabilities</Label>
            <h2 className="font-display uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl mt-6 leading-[0.95] text-white">
              Everything your<br />facility needs.
            </h2>
          </div>
          <p className="max-w-sm text-zinc-400 font-light">
            Offices and warehouses only — no restaurants. One accountable partner
            for every cleaning discipline.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-px bg-border border border-border">
          {/* Service list */}
          <div className="lg:col-span-7 bg-obsidian">
            {SERVICES.map((s) => (
              <button
                key={s.id}
                data-testid={`service-item-${s.id}`}
                onMouseEnter={() => setActive(s.id)}
                onClick={() => setActive(s.id)}
                className={`group w-full text-left px-6 lg:px-10 py-8 border-b border-border last:border-b-0 transition-colors duration-300 ${
                  active === s.id ? "bg-elevated" : "hover:bg-surface"
                }`}
              >
                <div className="flex items-start justify-between gap-6">
                  <div className="flex items-start gap-5">
                    <span
                      className={`font-display text-sm pt-2 tracking-widest transition-colors ${
                        active === s.id ? "text-acid" : "text-zinc-600"
                      }`}
                    >
                      {s.index}
                    </span>
                    <div>
                      <h3
                        className={`font-display uppercase text-2xl lg:text-4xl tracking-tight transition-colors ${
                          active === s.id ? "text-white" : "text-zinc-500"
                        }`}
                      >
                        {s.title}
                      </h3>
                      <p className="mt-2 text-sm text-zinc-500 max-w-md">
                        {s.short}
                      </p>
                    </div>
                  </div>
                  <ArrowUpRight
                    className={`shrink-0 mt-2 transition-all duration-300 ${
                      active === s.id
                        ? "text-acid translate-x-0 translate-y-0 opacity-100"
                        : "text-zinc-600 -translate-x-2 translate-y-2 opacity-0 group-hover:opacity-100"
                    }`}
                    size={28}
                  />
                </div>
              </button>
            ))}
          </div>

          {/* Spotlight preview */}
          <div className="lg:col-span-5 bg-obsidian relative min-h-[420px] lg:min-h-full overflow-hidden">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeService.id}
                initial={{ opacity: 0, scale: 1.05 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                className="absolute inset-0"
              >
                <img
                  src={activeService.image}
                  alt={activeService.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/40 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <p className="text-zinc-200 font-light leading-relaxed mb-5">
                    {activeService.description}
                  </p>
                  <div className="flex flex-wrap gap-2 mb-6">
                    {activeService.tags.map((t) => (
                      <span
                        key={t}
                        className="text-[10px] uppercase tracking-[0.15em] border border-white/20 text-zinc-300 px-3 py-1.5"
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                  <button
                    data-testid={`service-quote-${activeService.id}`}
                    onClick={() => scrollToId("quote")}
                    className="inline-flex items-center gap-2 text-acid text-xs uppercase tracking-[0.2em] font-bold hover:gap-4 transition-all"
                  >
                    Enquire about this <ArrowUpRight size={14} />
                  </button>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}
